import { useState, useEffect } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import Input from '../input/Input';
import DropDown from '../dropdown/DropDown';
import P from '../texts/p/P';
import { toast } from 'react-toastify';
import { createFund, updateFund } from '../../services/GuaranteesService';

/** Fila seleccionada para edición; null/undefined = alta */
export interface FundToEdit {
  id: number;
  name: string;
  is_tax_deductible: boolean;
}

interface NewFundModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  fundToEdit?: FundToEdit | null;
}

const NewFundModal = ({
  isOpen,
  onClose,
  onSuccess,
  fundToEdit = null,
}: NewFundModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const [name, setName] = useState<string>('');
  const [isTaxDeductible, setIsTaxDeductible] = useState<boolean | null>(null);

  const isEditMode = Boolean(fundToEdit);

  const resetForm = () => {
    setName('');
    setIsTaxDeductible(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    if (fundToEdit) {
      setName(fundToEdit.name ?? '');
      setIsTaxDeductible(
        fundToEdit.is_tax_deductible != null ? fundToEdit.is_tax_deductible : null
      );
    } else {
      resetForm();
    }
  }, [isOpen, fundToEdit]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!name || name.trim() === '') {
      toast.error('Ingrese el nombre del fondo');
      return;
    }

    if (isTaxDeductible === null) {
      toast.error('Seleccione si es desgravable');
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = {
        name: name.trim(),
        is_tax_deductible: isTaxDeductible,
      };

      const response =
        isEditMode && fundToEdit
          ? await updateFund(fundToEdit.id, requestBody)
          : await createFund(requestBody);

      if (response.success) {
        toast.success(
          isEditMode ? 'Fondo actualizado exitosamente' : 'Fondo creado exitosamente'
        );
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMsg =
          'message' in response
            ? response.message
            : isEditMode
              ? 'Error al actualizar fondo'
              : 'Error al crear fondo';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error(isEditMode ? 'Error updating fund:' : 'Error creating fund:', error);
      toast.error(isEditMode ? 'Error al actualizar fondo' : 'Error al crear fondo');
    } finally {
      setIsLoading(false);
    }
  };

  const taxDeductibleItems = [
    { label: 'SI', disabled: false },
    { label: 'NO', disabled: false },
  ];

  const getSelectedTaxDeductibleName = () => {
    if (isTaxDeductible === null) return 'Seleccionar';
    return isTaxDeductible ? 'SI' : 'NO';
  };

  const handleTaxDeductibleChange = (selectedLabel: string) => {
    setIsTaxDeductible(selectedLabel === 'SI');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Editar fondo' : 'Añadir nuevo fondo'}
      width='600px'
      primaryText={isEditMode ? 'Guardar' : 'Agregar'}
      primaryVariant='primary'
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={isLoading}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          <Input
            label="Nombre *"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
            size="md"
          />

          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Es desgravable *
            </P>
            <Card margin={{ top: '2' }}>
              <DropDown
                id="is_tax_deductible"
                title={getSelectedTaxDeductibleName()}
                items={taxDeductibleItems}
                onChange={handleTaxDeductibleChange}
                size="md"
              />
            </Card>
          </Card>
        </div>
      </Card>
    </Modal>
  );
};

export default NewFundModal;
